import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuthStore } from '../stores/authStore'

export interface InvitoTeam {
  id: string
  team: { id: string; nome: string }
  invitato_da: { nome: string | null; cognome: string | null } | null
  messaggio: string | null
  created_at: string
  expires_at: string
}

interface RispostaInviti {
  inviti: InvitoTeam[]
  totale: number
}

/**
 * Hook che recupera gli inviti team in sospeso per l'utente loggato.
 *
 * Quando un manager invita un SDR già registrato, il backend crea una
 * riga in `team_invites` con stato='in_attesa'. Al login del'SDR
 * questo hook li carica e l'UI (`AppShell`) mostra una modale di
 * accept/reject.
 *
 * `enabled` solo per SDR: manager/superadmin non possono ricevere
 * inviti team. `staleTime: Infinity` perché la lista cambia solo a
 * fronte di azioni esplicite (accetta/rifiuta) che invalidano la query.
 */
export function useInviti() {
  const utente = useAuthStore((s) => s.utente)
  const enabled = !!utente && utente.ruolo === 'sdr'

  const q = useQuery({
    queryKey: ['inviti-team'],
    queryFn: async () =>
      (await api.get<{ dati: RispostaInviti }>('/auth/inviti')).data.dati!,
    enabled,
    staleTime: Infinity,
  })

  const inviti = q.data?.inviti ?? []
  return {
    inviti,
    haInvitiInAttesa: inviti.length > 0,
    isLoading: q.isLoading,
  }
}

/**
 * Mutation per accettare/rifiutare un invito. Al successo invalida sia
 * gli inviti (per far sparire la modale) sia il profilo utente (per
 * aggiornare il team_id in cache).
 */
export function useRispondiInvito() {
  const queryClient = useQueryClient()

  const invalida = (): void => {
    queryClient.invalidateQueries({ queryKey: ['inviti-team'] })
    queryClient.invalidateQueries({ queryKey: ['profilo'] })
  }

  const accetta = useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ dati: { team: { id: string; nome: string }; messaggio: string } }>(
        `/auth/inviti/${id}/accetta`,
      )).data.dati!,
    onSuccess: invalida,
  })

  const rifiuta = useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ dati: { messaggio: string } }>(`/auth/inviti/${id}/rifiuta`)).data.dati!,
    onSuccess: invalida,
  })

  return { accetta, rifiuta }
}
